import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FetchError, GetMeetingsForTeamDto } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { configuration } from "../../../configuration";
import { queryKeys } from "../queryKeys";
import { useSignalR } from "../../../hooks";
import { optimisticallyUpdateQueryData } from "../utilities";

type Variables = {
  meetingGuid: string;
  teamGuid?: string;
};

const useDeleteMeetingRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function deleteMeetingRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();

    const response = await fetch(`${configuration.apiRootUrl}/meetings/${variables.meetingGuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "delete",
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { deleteMeetingRequest };
};

const useDeleteMeeting = () => {
  const { deleteMeetingRequest } = useDeleteMeetingRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: deleteMeetingRequest,
    onMutate: async (variables) => {
      var cachedTeamData: GetMeetingsForTeamDto | undefined;

      if (variables.teamGuid) {
        cachedTeamData = await optimisticallyUpdateQueryData<GetMeetingsForTeamDto>(
          queryClient,
          queryKeys.meetings.getMeetingsForTeam(variables.teamGuid),
          (cachedData) => ({
            meetings: cachedData.meetings.filter((meeting) => meeting.guid !== variables.meetingGuid),
          })
        );
      }

      return { cachedTeamData };
    },
    onSuccess: (_data, variables) => {
      if (variables.teamGuid) {
        signalR.sendRefetchTeamMeetingsList(variables.teamGuid);
      }
    },
    onError: (_error, variables, context) => {
      if (variables.teamGuid) {
        queryClient.setQueryData(queryKeys.meetings.getMeetingsForTeam(variables.teamGuid), context?.cachedTeamData);
      }
    },
    onSettled: (_data, _error, variables) => {
      if (variables.teamGuid) {
        queryClient.invalidateQueries(queryKeys.meetings.getMeetingsForTeam(variables.teamGuid));
      }

      queryClient.invalidateQueries(queryKeys.meetings.getOneOnOneMeetingsForAllUsers());
    },
  });
};

export { useDeleteMeeting };
